import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, StorageFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import type { SimulationConfig } from '../../config/schema';
import { computeCorners, elasticPackA, elasticPackB } from '../../config/corners';
import { SculptureStateA, SculptureStateB, SculptureParams, SculptureDivParams, DataCell } from './types';
import { systemDeriv, computeSculptureTip, sculptureDivergence } from './deriv';
import { hash, samplePerturb } from '../shared/hash';
import { vizModeToNumber } from '../shared/constants';
import { DualStateSystem } from '../shared/DualStateSystem';
import type { GpuPipelinesResult, DivGpuPipelinesResult, NeighborPipelinesResult } from '../shared/DualStateSystem';
import { buildSculptureNeighborPipelines } from './SculptureNeighbor';

export class SculptureSystem extends DualStateSystem<typeof SculptureStateA, typeof SculptureStateB, typeof SculptureParams, typeof SculptureDivParams, typeof DataCell> {
  readonly key = 'sculpture' as const;
  readonly stateSize = 32;

  createStateArrayA(cellCount: number): WgslArray<typeof SculptureStateA> { return d.arrayOf(SculptureStateA, cellCount); }
  createStateArrayB(cellCount: number): WgslArray<typeof SculptureStateB> { return d.arrayOf(SculptureStateB, cellCount); }
  createDataArray(cellCount: number): WgslArray<typeof DataCell> { return d.arrayOf(DataCell, cellCount); }
  paramsStruct(): typeof SculptureParams { return SculptureParams; }
  divParamsStruct(): typeof SculptureDivParams { return SculptureDivParams; }
  zeroStateA(): any[] {
    return Array.from({ length: this.cellCount }, () => ({ theta0: 0, omega0: 0, theta1: 0, omega1: 0 }));
  }
  zeroStateB(): any[] {
    return Array.from({ length: this.cellCount }, () => ({ theta2: 0, omega2: 0, theta3: 0, omega3: 0 }));
  }

  protected buildGpuPipelines(root: TgpuRoot, stateAa: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag, stateBa: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag): GpuPipelinesResult {
    const initLayout = tgpu.bindGroupLayout({
      stateA: { storage: this.StateArrayA, access: 'mutable' },
      stateB: { storage: this.StateArrayB, access: 'mutable' },
      params: { uniform: SculptureParams },
    });
    const stepLayout = tgpu.bindGroupLayout({
      currentA: { storage: this.StateArrayA }, currentB: { storage: this.StateArrayB },
      nextA: { storage: this.StateArrayA, access: 'mutable' }, nextB: { storage: this.StateArrayB, access: 'mutable' },
      params: { uniform: SculptureParams },
    });
    const accumulateLayout = tgpu.bindGroupLayout({
      currentA: { storage: this.StateArrayA }, currentB: { storage: this.StateArrayB },
      data: { storage: this.DataArray, access: 'mutable' },
      params: { uniform: SculptureParams },
    });

    const rk4Step = (t0: number, w0: number, t1: number, w1: number, t2: number, w2: number, t3: number, w3: number, sM: number, sL: number, sA: number, sR: number, sN: number, dt: number) => {
      'use gpu';
      const h = 0.5 * dt;
      const d1 = systemDeriv(t0, w0, t1, w1, t2, w2, t3, w3, sM, sL, sA, sR, sN);
      const d2 = systemDeriv(t0+h*d1.dt0, w0+h*d1.dw0, t1+h*d1.dt1, w1+h*d1.dw1, t2+h*d1.dt2, w2+h*d1.dw2, t3+h*d1.dt3, w3+h*d1.dw3, sM, sL, sA, sR, sN);
      const d3 = systemDeriv(t0+h*d2.dt0, w0+h*d2.dw0, t1+h*d2.dt1, w1+h*d2.dw1, t2+h*d2.dt2, w2+h*d2.dw2, t3+h*d2.dt3, w3+h*d2.dw3, sM, sL, sA, sR, sN);
      const d4 = systemDeriv(t0+dt*d3.dt0, w0+dt*d3.dw0, t1+dt*d3.dt1, w1+dt*d3.dw1, t2+dt*d3.dt2, w2+dt*d3.dw2, t3+dt*d3.dt3, w3+dt*d3.dw3, sM, sL, sA, sR, sN);
      const s6 = dt / 6.0;
      return {
        t0: t0+s6*(d1.dt0+2*d2.dt0+2*d3.dt0+d4.dt0), w0: w0+s6*(d1.dw0+2*d2.dw0+2*d3.dw0+d4.dw0),
        t1: t1+s6*(d1.dt1+2*d2.dt1+2*d3.dt1+d4.dt1), w1: w1+s6*(d1.dw1+2*d2.dw1+2*d3.dw1+d4.dw1),
        t2: t2+s6*(d1.dt2+2*d2.dt2+2*d3.dt2+d4.dt2), w2: w2+s6*(d1.dw2+2*d2.dw2+2*d3.dw2+d4.dw2),
        t3: t3+s6*(d1.dt3+2*d2.dt3+2*d3.dt3+d4.dt3), w3: w3+s6*(d1.dw3+2*d2.dw3+2*d3.dw3+d4.dw3),
      };
    };

    const initCell = (ci: number) => {
      'use gpu';
      const p = initLayout.$.params;
      const x = ci % d.u32(p.resolution); const y = ci / d.u32(p.resolution);
      const u = d.f32(x) / p.resolution; const v = d.f32(y) / p.resolution;
      const omu = 1 - u; const omv = 1 - v;
      initLayout.$.stateA[ci] = SculptureStateA({ theta0: omu*omv*p.cA00_t0+u*omv*p.cA10_t0+omu*v*p.cA01_t0+u*v*p.cA11_t0, omega0: omu*omv*p.cA00_w0+u*omv*p.cA10_w0+omu*v*p.cA01_w0+u*v*p.cA11_w0, theta1: omu*omv*p.cA00_t1+u*omv*p.cA10_t1+omu*v*p.cA01_t1+u*v*p.cA11_t1, omega1: omu*omv*p.cA00_w1+u*omv*p.cA10_w1+omu*v*p.cA01_w1+u*v*p.cA11_w1 });
      initLayout.$.stateB[ci] = SculptureStateB({ theta2: omu*omv*p.cB00_t2+u*omv*p.cB10_t2+omu*v*p.cB01_t2+u*v*p.cB11_t2, omega2: omu*omv*p.cB00_w2+u*omv*p.cB10_w2+omu*v*p.cB01_w2+u*v*p.cB11_w2, theta3: omu*omv*p.cB00_t3+u*omv*p.cB10_t3+omu*v*p.cB01_t3+u*v*p.cB11_t3, omega3: omu*omv*p.cB00_w3+u*omv*p.cB10_w3+omu*v*p.cB01_w3+u*v*p.cB11_w3 });
    };
    const stepCell = (ci: number) => {
      'use gpu';
      const sa = stepLayout.$.currentA[ci]; const sb = stepLayout.$.currentB[ci]; const p = stepLayout.$.params;
      const n = rk4Step(sa.theta0, sa.omega0, sa.theta1, sa.omega1, sb.theta2, sb.omega2, sb.theta3, sb.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, p.dt);
      stepLayout.$.nextA[ci] = SculptureStateA({ theta0: n.t0, omega0: n.w0, theta1: n.t1, omega1: n.w1 });
      stepLayout.$.nextB[ci] = SculptureStateB({ theta2: n.t2, omega2: n.w2, theta3: n.t3, omega3: n.w3 });
    };
    const accumulateCell = (ci: number) => {
      'use gpu';
      const sa = accumulateLayout.$.currentA[ci]; const sb = accumulateLayout.$.currentB[ci]; const data = accumulateLayout.$.data[ci]; const p = accumulateLayout.$.params;
      const vm = p.vizMode;
      if (vm < 0.5) {
        const tip = computeSculptureTip(sa.theta0, sa.theta1, sb.theta2, sb.theta3, p.scA0, p.scL0, p.scR, p.scN);
        if (data.a > 0.5) { const dx = tip.x - data.r; const dy = tip.y - data.g; const dist = std.sqrt(dx*dx+dy*dy); accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: data.b+dist, a: 1 }); }
        else { accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: 0, a: 1 }); }
      } else if (vm < 1.5) {
        const tip = computeSculptureTip(sa.theta0, sa.theta1, sb.theta2, sb.theta3, p.scA0, p.scL0, p.scR, p.scN);
        accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: 0, a: 1 });
      } else if (vm < 3.5) {
        const tip = computeSculptureTip(sa.theta0, sa.theta1, sb.theta2, sb.theta3, p.scA0, p.scL0, p.scR, p.scN);
        if (data.a > 0.5) { const dx = tip.x - data.r; const dy = tip.y - data.g; const dist = std.sqrt(dx*dx+dy*dy); accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: data.b+dist, a: 1 }); }
        else { accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: 0, a: 1 }); }
      } else {
        const res = d.u32(p.resolution);
        const cx = ci % res; const cy = ci / res;
        const tip = computeSculptureTip(sa.theta0, sa.theta1, sb.theta2, sb.theta3, p.scA0, p.scL0, p.scR, p.scN);
        const nx0 = std.min(cx + 1, res - 1);
        const nx1 = std.max(d.i32(cx) - 1, 0);
        const ny0 = std.min(cy + 1, res - 1);
        const ny1 = std.max(d.i32(cy) - 1, 0);
        const sa0 = accumulateLayout.$.currentA[d.u32(ny0)*res+cx]; const sb0 = accumulateLayout.$.currentB[d.u32(ny0)*res+cx];
        const sa1 = accumulateLayout.$.currentA[d.u32(ny1)*res+cx]; const sb1 = accumulateLayout.$.currentB[d.u32(ny1)*res+cx];
        const sa2 = accumulateLayout.$.currentA[cy*res+nx0]; const sb2 = accumulateLayout.$.currentB[cy*res+nx0];
        const sa3 = accumulateLayout.$.currentA[cy*res+d.u32(nx1)]; const sb3 = accumulateLayout.$.currentB[cy*res+d.u32(nx1)];
        const t0 = computeSculptureTip(sa0.theta0, sa0.theta1, sb0.theta2, sb0.theta3, p.scA0, p.scL0, p.scR, p.scN);
        const t1 = computeSculptureTip(sa1.theta0, sa1.theta1, sb1.theta2, sb1.theta3, p.scA0, p.scL0, p.scR, p.scN);
        const t2 = computeSculptureTip(sa2.theta0, sa2.theta1, sb2.theta2, sb2.theta3, p.scA0, p.scL0, p.scR, p.scN);
        const t3 = computeSculptureTip(sa3.theta0, sa3.theta1, sb3.theta2, sb3.theta3, p.scA0, p.scL0, p.scR, p.scN);
        const avgDx = (tip.x-t0.x + tip.x-t1.x + tip.x-t2.x + tip.x-t3.x) * 0.25;
        const avgDy = (tip.y-t0.y + tip.y-t1.y + tip.y-t2.y + tip.y-t3.y) * 0.25;
        const avgDist = std.sqrt(avgDx*avgDx + avgDy*avgDy);
        if (vm < 4.5) {
          accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: avgDist, a: 1 });
        } else {
          if (data.a > 0.5) {
            accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: data.b + avgDist, a: 1 });
          } else {
            accumulateLayout.$.data[ci] = DataCell({ r: tip.x, g: tip.y, b: avgDist, a: 1 });
          }
        }
      }
    };

    return {
      initLayout, stepLayout, accumulateLayout,
      initPipeline: root.createGuardedComputePipeline(initCell),
      stepPipeline: root.createGuardedComputePipeline(stepCell),
      accumulatePipeline: root.createGuardedComputePipeline(accumulateCell),
      initBG: root.createBindGroup(initLayout, { stateA: stateAa, stateB: stateBa, params: this.paramsBuffer }),
      stepFwd: root.createBindGroup(stepLayout, { currentA: stateAa, currentB: stateBa, nextA: stateAb, nextB: stateBb, params: this.paramsBuffer }),
      stepBwd: root.createBindGroup(stepLayout, { currentA: stateAb, currentB: stateBb, nextA: stateAa, nextB: stateBa, params: this.paramsBuffer }),
      accA: root.createBindGroup(accumulateLayout, { currentA: stateAa, currentB: stateBa, data: this.dataBuf, params: this.paramsBuffer }),
      accB: root.createBindGroup(accumulateLayout, { currentA: stateAb, currentB: stateBb, data: this.dataBuf, params: this.paramsBuffer }),
    };
  }

  protected buildDivGpuPipelines(root: TgpuRoot, stateAa: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag, stateBa: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag): DivGpuPipelinesResult {
    const divInitLayout = tgpu.bindGroupLayout({
      baseStateA: { storage: this.StateArrayA, access: 'mutable' }, baseStateB: { storage: this.StateArrayB, access: 'mutable' },
      pertStateA: { storage: this.StateArrayA, access: 'mutable' }, pertStateB: { storage: this.StateArrayB, access: 'mutable' },
      divData: { storage: this.DataArray, access: 'mutable' }, params: { uniform: SculptureDivParams },
    });
    const divStepLayout = tgpu.bindGroupLayout({
      baseCurrentA: { storage: this.StateArrayA }, baseCurrentB: { storage: this.StateArrayB },
      pertCurrentA: { storage: this.StateArrayA }, pertCurrentB: { storage: this.StateArrayB },
      baseNextA: { storage: this.StateArrayA, access: 'mutable' }, baseNextB: { storage: this.StateArrayB, access: 'mutable' },
      pertNextA: { storage: this.StateArrayA, access: 'mutable' }, pertNextB: { storage: this.StateArrayB, access: 'mutable' },
      divData: { storage: this.DataArray, access: 'mutable' }, params: { uniform: SculptureDivParams },
    });

    const divRk4 = (t0: number, w0: number, t1: number, w1: number, t2: number, w2: number, t3: number, w3: number, sM: number, sL: number, sA: number, sR: number, sN: number, dt: number) => {
      'use gpu';
      const h = 0.5 * dt;
      const d1 = systemDeriv(t0, w0, t1, w1, t2, w2, t3, w3, sM, sL, sA, sR, sN);
      const d2 = systemDeriv(t0+h*d1.dt0, w0+h*d1.dw0, t1+h*d1.dt1, w1+h*d1.dw1, t2+h*d1.dt2, w2+h*d1.dw2, t3+h*d1.dt3, w3+h*d1.dw3, sM, sL, sA, sR, sN);
      const d3 = systemDeriv(t0+h*d2.dt0, w0+h*d2.dw0, t1+h*d2.dt1, w1+h*d2.dw1, t2+h*d2.dt2, w2+h*d2.dw2, t3+h*d2.dt3, w3+h*d2.dw3, sM, sL, sA, sR, sN);
      const d4 = systemDeriv(t0+dt*d3.dt0, w0+dt*d3.dw0, t1+dt*d3.dt1, w1+dt*d3.dw1, t2+dt*d3.dt2, w2+dt*d3.dw2, t3+dt*d3.dt3, w3+dt*d3.dw3, sM, sL, sA, sR, sN);
      const s6 = dt / 6.0;
      return {
        t0: t0+s6*(d1.dt0+2*d2.dt0+2*d3.dt0+d4.dt0), w0: w0+s6*(d1.dw0+2*d2.dw0+2*d3.dw0+d4.dw0),
        t1: t1+s6*(d1.dt1+2*d2.dt1+2*d3.dt1+d4.dt1), w1: w1+s6*(d1.dw1+2*d2.dw1+2*d3.dw1+d4.dw1),
        t2: t2+s6*(d1.dt2+2*d2.dt2+2*d3.dt2+d4.dt2), w2: w2+s6*(d1.dw2+2*d2.dw2+2*d3.dw2+d4.dw2),
        t3: t3+s6*(d1.dt3+2*d2.dt3+2*d3.dt3+d4.dt3), w3: w3+s6*(d1.dw3+2*d2.dw3+2*d3.dw3+d4.dw3),
      };
    };

    const divInitCell = (ci: number) => {
      'use gpu';
      const p = divInitLayout.$.params;
      const x = ci % d.u32(p.resolution); const y = ci / d.u32(p.resolution);
      const u = d.f32(x) / p.resolution; const v = d.f32(y) / p.resolution;
      const omu = 1 - u; const omv = 1 - v;
      const t0 = omu*omv*p.cA00_t0+u*omv*p.cA10_t0+omu*v*p.cA01_t0+u*v*p.cA11_t0;
      const w0 = omu*omv*p.cA00_w0+u*omv*p.cA10_w0+omu*v*p.cA01_w0+u*v*p.cA11_w0;
      const t1 = omu*omv*p.cA00_t1+u*omv*p.cA10_t1+omu*v*p.cA01_t1+u*v*p.cA11_t1;
      const ow1 = omu*omv*p.cA00_w1+u*omv*p.cA10_w1+omu*v*p.cA01_w1+u*v*p.cA11_w1;
      const t2 = omu*omv*p.cB00_t2+u*omv*p.cB10_t2+omu*v*p.cB01_t2+u*v*p.cB11_t2;
      const w2 = omu*omv*p.cB00_w2+u*omv*p.cB10_w2+omu*v*p.cB01_w2+u*v*p.cB11_w2;
      const t3 = omu*omv*p.cB00_t3+u*omv*p.cB10_t3+omu*v*p.cB01_t3+u*v*p.cB11_t3;
      const w3 = omu*omv*p.cB00_w3+u*omv*p.cB10_w3+omu*v*p.cB01_w3+u*v*p.cB11_w3;
      divInitLayout.$.baseStateA[ci] = SculptureStateA({ theta0: t0, omega0: w0, theta1: t1, omega1: ow1 });
      divInitLayout.$.baseStateB[ci] = SculptureStateB({ theta2: t2, omega2: w2, theta3: t3, omega3: w3 });
      const px = d.f32(x) / p.resolution; const py = d.f32(y) / p.resolution;
      const h0 = samplePerturb(px*1000+p.seed, py*1000+p.seed, p.perturb, p.perturbMode);
      const h2 = samplePerturb(px*1000+100+p.seed, py*1000+p.seed, p.perturb, p.perturbMode);
      divInitLayout.$.pertStateA[ci] = SculptureStateA({ theta0: t0+h0, omega0: w0, theta1: t1, omega1: ow1 });
      divInitLayout.$.pertStateB[ci] = SculptureStateB({ theta2: t2+h2, omega2: w2, theta3: t3, omega3: w3 });
      divInitLayout.$.divData[ci] = DataCell({ r: 0, g: 0, b: 0, a: 1 });
    };

    const divStepCell = (ci: number) => {
      'use gpu';
      const p = divStepLayout.$.params;
      const bsa = divStepLayout.$.baseCurrentA[ci]; const bsb = divStepLayout.$.baseCurrentB[ci];
      const psa = divStepLayout.$.pertCurrentA[ci]; const psb = divStepLayout.$.pertCurrentB[ci];
      const bn = divRk4(bsa.theta0, bsa.omega0, bsa.theta1, bsa.omega1, bsb.theta2, bsb.omega2, bsb.theta3, bsb.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, p.dt);
      divStepLayout.$.baseNextA[ci] = SculptureStateA({ theta0: bn.t0, omega0: bn.w0, theta1: bn.t1, omega1: bn.w1 });
      divStepLayout.$.baseNextB[ci] = SculptureStateB({ theta2: bn.t2, omega2: bn.w2, theta3: bn.t3, omega3: bn.w3 });
      const pn = divRk4(psa.theta0, psa.omega0, psa.theta1, psa.omega1, psb.theta2, psb.omega2, psb.theta3, psb.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, p.dt);
      divStepLayout.$.pertNextA[ci] = SculptureStateA({ theta0: pn.t0, omega0: pn.w0, theta1: pn.t1, omega1: pn.w1 });
      divStepLayout.$.pertNextB[ci] = SculptureStateB({ theta2: pn.t2, omega2: pn.w2, theta3: pn.t3, omega3: pn.w3 });
      const div = sculptureDivergence(bn.t0, bn.w0, bn.t1, bn.w1, bn.t2, bn.w2, bn.t3, bn.w3, pn.t0, pn.w0, pn.t1, pn.w1, pn.t2, pn.w2, pn.t3, pn.w3, p.scN);
      const data = divStepLayout.$.divData[ci];
      if (data.g < 0.5) {
        if (div > 1.0) { divStepLayout.$.divData[ci] = DataCell({ r: p.frameCounter, g: 1, b: div, a: 1 }); }
      } else {
        divStepLayout.$.divData[ci] = DataCell({ r: data.r, g: 1, b: div, a: 1 });
      }
    };

    return {
      divInitLayout, divStepLayout,
      divInitPipeline: root.createGuardedComputePipeline(divInitCell),
      divStepPipeline: root.createGuardedComputePipeline(divStepCell),
      divInitBG: root.createBindGroup(divInitLayout, { baseStateA: stateAa, baseStateB: stateBa, pertStateA: this.pertStateAa, pertStateB: this.pertStateBa, divData: this.divDataBuf, params: this.divParamsBuffer }),
      divStepFwd: root.createBindGroup(divStepLayout, { baseCurrentA: stateAa, baseCurrentB: stateBa, pertCurrentA: this.pertStateAa, pertCurrentB: this.pertStateBa, baseNextA: stateAb, baseNextB: stateBb, pertNextA: this.pertStateAb, pertNextB: this.pertStateBb, divData: this.divDataBuf, params: this.divParamsBuffer }),
      divStepBwd: root.createBindGroup(divStepLayout, { baseCurrentA: stateAb, baseCurrentB: stateBb, pertCurrentA: this.pertStateAb, pertCurrentB: this.pertStateBb, baseNextA: stateAa, baseNextB: stateBa, pertNextA: this.pertStateAa, pertNextB: this.pertStateBa, divData: this.divDataBuf, params: this.divParamsBuffer }),
    };
  }

  buildParamsData(config: SimulationConfig): Record<string, number> {
    this.lastConfig = config;
    return packScParams(config);
  }

  buildDivParamsData(config: SimulationConfig, seed: number, perturb: number): Record<string, number> & { frameCounter: number } {
    return { ...packScParams(config), seed, perturb, perturbMode: config.perturbDistribution === 'gaussian' ? 1 : 0, frameCounter: 0 };
  }

  protected buildClosenessOfReturnPipeline(
    root: TgpuRoot, stateAa: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag,
    snapA: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag, snapB: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag,
    dataBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag,
  ): import('../shared/DualStateSystem').CORPipelineResult {
    const TTRU = d.struct({ currentIter: d.f32, minImprovement: d.f32, _p0: d.f32, _p1: d.f32 });
    const ttrLayout = tgpu.bindGroupLayout({
      curA: { storage: this.StateArrayA }, curB: { storage: this.StateArrayB },
      nextA: { storage: this.StateArrayA, access: 'mutable' }, nextB: { storage: this.StateArrayB, access: 'mutable' },
      snapA: { storage: this.StateArrayA }, snapB: { storage: this.StateArrayB },
      data: { storage: this.DataArray, access: 'mutable' }, params: { uniform: SculptureParams }, ttr: { uniform: TTRU },
    });
    const tl = ttrLayout;
    const rk4 = (t0: number, w0: number, t1: number, w1: number, t2: number, w2: number, t3: number, w3: number, sM: number, sL: number, sA: number, sR: number, sN: number, dt: number) => {
      'use gpu';
      const h = 0.5 * dt;
      const d1 = systemDeriv(t0, w0, t1, w1, t2, w2, t3, w3, sM, sL, sA, sR, sN);
      const d2 = systemDeriv(t0+h*d1.dt0, w0+h*d1.dw0, t1+h*d1.dt1, w1+h*d1.dw1, t2+h*d1.dt2, w2+h*d1.dw2, t3+h*d1.dt3, w3+h*d1.dw3, sM, sL, sA, sR, sN);
      const d3 = systemDeriv(t0+h*d2.dt0, w0+h*d2.dw0, t1+h*d2.dt1, w1+h*d2.dw1, t2+h*d2.dt2, w2+h*d2.dw2, t3+h*d2.dt3, w3+h*d2.dw3, sM, sL, sA, sR, sN);
      const d4 = systemDeriv(t0+dt*d3.dt0, w0+dt*d3.dw0, t1+dt*d3.dt1, w1+dt*d3.dw1, t2+dt*d3.dt2, w2+dt*d3.dw2, t3+dt*d3.dt3, w3+dt*d3.dw3, sM, sL, sA, sR, sN);
      const s = dt / 6.0;
      return { t0: t0+s*(d1.dt0+2*d2.dt0+2*d3.dt0+d4.dt0), w0: w0+s*(d1.dw0+2*d2.dw0+2*d3.dw0+d4.dw0), t1: t1+s*(d1.dt1+2*d2.dt1+2*d3.dt1+d4.dt1), w1: w1+s*(d1.dw1+2*d2.dw1+2*d3.dw1+d4.dw1), t2: t2+s*(d1.dt2+2*d2.dt2+2*d3.dt2+d4.dt2), w2: w2+s*(d1.dw2+2*d2.dw2+2*d3.dw2+d4.dw2), t3: t3+s*(d1.dt3+2*d2.dt3+2*d3.dt3+d4.dt3), w3: w3+s*(d1.dw3+2*d2.dw3+2*d3.dw3+d4.dw3) };
    };
    const TWO_PI = 6.28318530718;
    const cd = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };
    const ttrCell = (ci: number) => {
      'use gpu';
      const cA = tl.$.curA[ci]; const cB = tl.$.curB[ci]; const p = tl.$.params;
      const n = rk4(cA.theta0, cA.omega0, cA.theta1, cA.omega1, cB.theta2, cB.omega2, cB.theta3, cB.omega3, p.scM0, p.scL0, p.scA0, p.scR, p.scN, p.dt);
      tl.$.nextA[ci] = SculptureStateA({ theta0: n.t0, omega0: n.w0, theta1: n.t1, omega1: n.w1 });
      tl.$.nextB[ci] = SculptureStateB({ theta2: n.t2, omega2: n.w2, theta3: n.t3, omega3: n.w3 });
      const snA = tl.$.snapA[ci]; const snB = tl.$.snapB[ci];
      const d0=cd(n.t0-snA.theta0); const dw0=n.w0-snA.omega0; const d1=cd(n.t1-snA.theta1); const dw1=n.w1-snA.omega1;
      const d2=cd(n.t2-snB.theta2); const dw2=n.w2-snB.omega2; const d3=cd(n.t3-snB.theta3); const dw3=n.w3-snB.omega3;
      const dist = std.sqrt(d0*d0+dw0*dw0+d1*d1+dw1*dw1+d2*d2+dw2*dw2+d3*d3+dw3*dw3);
      const data = tl.$.data[ci]; const ttr = tl.$.ttr;
      if (data.a < 0.5) { tl.$.data[ci] = DataCell({ r: ttr.currentIter, g: 0, b: dist, a: 1 }); }
      else if (dist < data.b - ttr.minImprovement) { tl.$.data[ci] = DataCell({ r: ttr.currentIter, g: data.g, b: dist, a: data.a }); }
    };
    return { ttrPipeline: root.createGuardedComputePipeline(ttrCell), ttrFwd: root.createBindGroup(ttrLayout, { curA: stateAa as never, curB: stateBa as never, nextA: stateAb as never, nextB: stateBb as never, snapA: snapA as never, snapB: snapB as never, data: dataBuf as never, params: this.paramsBuffer as never, ttr: this.ttrUniformsBuf as never }), ttrBwd: root.createBindGroup(ttrLayout, { curA: stateAb as never, curB: stateBb as never, nextA: stateAa as never, nextB: stateBa as never, snapA: snapA as never, snapB: snapB as never, data: dataBuf as never, params: this.paramsBuffer as never, ttr: this.ttrUniformsBuf as never }) };
  }

  protected buildNeighborPipelines(
    root: TgpuRoot,
    stateAa: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag, stateAb: TgpuBuffer<WgslArray<typeof SculptureStateA>> & StorageFlag,
    stateBa: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag, stateBb: TgpuBuffer<WgslArray<typeof SculptureStateB>> & StorageFlag,
  ): NeighborPipelinesResult {
    return buildSculptureNeighborPipelines({
      root,
      StateArrayA: this.StateArrayA, StateArrayB: this.StateArrayB, DataArray: this.DataArray,
      NbStackArrayA: this.nbStackArrayA, NbStackArrayB: this.nbStackArrayB,
      paramsBuffer: this.paramsBuffer, nbParamsBuffer: this.nbParamsBuffer,
      baseAa: stateAa, baseAb: stateAb, baseBa: stateBa, baseBb: stateBb,
      nbPertAa: this.nbPertAa, nbPertAb: this.nbPertAb, nbPertBa: this.nbPertBa, nbPertBb: this.nbPertBb,
      nbDataBuf: this.nbDataBuf,
    });
  }
}

function packScParams(config: SimulationConfig): Record<string, number> {
  const corners = computeCorners(config);
  const ca = (i: number) => elasticPackA(corners[i]);
  const cb = (i: number) => elasticPackB(corners[i]);
  const ca00 = ca(0); const ca10 = ca(1); const ca01 = ca(2); const ca11 = ca(3);
  const cb00 = cb(0); const cb10 = cb(1); const cb01 = cb(2); const cb11 = cb(3);
  return {
    scM0: config.sculptureWeight, scL0: config.sculptureRod, scA0: config.sculptureAxle,
    scR: config.sculptureReduction, scN: config.sculptureN,
    dt: config.dt, resolution: config.resolution,
    vizMode: vizModeToNumber(config.vizMode),
    cA00_t0: ca00[0], cA00_w0: ca00[1], cA00_t1: ca00[2], cA00_w1: ca00[3],
    cA10_t0: ca10[0], cA10_w0: ca10[1], cA10_t1: ca10[2], cA10_w1: ca10[3],
    cA01_t0: ca01[0], cA01_w0: ca01[1], cA01_t1: ca01[2], cA01_w1: ca01[3],
    cA11_t0: ca11[0], cA11_w0: ca11[1], cA11_t1: ca11[2], cA11_w1: ca11[3],
    cB00_t2: cb00[0], cB00_w2: cb00[1], cB00_t3: cb00[2], cB00_w3: cb00[3],
    cB10_t2: cb10[0], cB10_w2: cb10[1], cB10_t3: cb10[2], cB10_w3: cb10[3],
    cB01_t2: cb01[0], cB01_w2: cb01[1], cB01_t3: cb01[2], cB01_w3: cb01[3],
    cB11_t2: cb11[0], cB11_w2: cb11[1], cB11_t3: cb11[2], cB11_w3: cb11[3],
  };
}